import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet } from 'react-router';

import { ConfigProvider } from 'antd';

import { DocumentTitleProvider } from '../components/Helmet';
import NeedHelpPromo from '../components/NeedHelpPromo';
import TableEmpty from '../components/TableEmpty';

import DisplaySizeContext, { DisplaySizeType } from '../context/DisplaySize';
import { ThemeProvider } from '../context/Theme';
import clientActions from '../modules/client/actions';
import { useHomeMessageId } from '../modules/home/hooks/useHomeMessageId';
import { cabinetTheme } from './AntdProviders';

import { AppDispatch, ReduxState } from '../types/app';

const getDisplaySize = (): DisplaySizeType => {
  if (typeof window !== 'undefined') {
    let width = Math.max(
      document?.documentElement.clientWidth,
      window?.innerWidth || 0
    );

    if (window?.screen && width > window.screen.width) {
      width = window.screen.width;
    }

    if (width <= 576) {
      return 'mobile';
    }

    if (width <= 950) {
      return 'tablet';
    }

    if (width <= 950) {
      return 'tablet';
    }

    if (width < 1030) {
      return 'desktop';
    }
  }

  return 'desktopLg';
};

const App = () => {
  const intl = useIntl();
  const dispatch = useDispatch<AppDispatch>();
  const isLoggedIn = useSelector(
    (state: ReduxState) => state.Client.isLoggedIn
  );
  const homeMessageId = useHomeMessageId();
  const [displaySize, setDisplaySize] = useState<DisplaySizeType>('desktopLg');

  const handleWindowSizeChange = useCallback(() => {
    const newDisplaySize = getDisplaySize();

    setDisplaySize((currentDisplaySize) =>
      currentDisplaySize === newDisplaySize
        ? currentDisplaySize
        : newDisplaySize
    );
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    handleWindowSizeChange();
    window.addEventListener('resize', handleWindowSizeChange);

    return () => {
      window.removeEventListener('resize', handleWindowSizeChange);
    };
  }, [handleWindowSizeChange]);

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(clientActions.TotalBalance.loadIfNeeded());
    }
  }, [dispatch, isLoggedIn]);

  return (
    <ConfigProvider theme={cabinetTheme} renderEmpty={() => <TableEmpty />}>
      <DocumentTitleProvider
        htmlAttributes={{ lang: intl.locale }}
        titleTemplate={intl.formatMessage({
          id: homeMessageId('meta.titleTemplate'),
        })}
        defaultTitle={intl.formatMessage({ id: homeMessageId('meta.title') })}
        meta={[
          {
            name: 'description',
            content: intl.formatMessage({
              id: homeMessageId('meta.description'),
            }),
          },
        ]}
      >
        <DisplaySizeContext.Provider value={displaySize}>
          <ThemeProvider>
            <Outlet />
            <NeedHelpPromo />
          </ThemeProvider>
        </DisplaySizeContext.Provider>
      </DocumentTitleProvider>
    </ConfigProvider>
  );
};

export default App;
